import React, { useEffect, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Badge } from '../ui/badge';
import { LineDiff } from './LineDiff';
import { BomLineageTimeline } from './BomLineageTimeline';

const fmt = (s) => (s ? new Date(s).toLocaleString() : '—');

const VersionCard = ({ v, idx }) => (
  <div className={`rounded-md border p-3 ${v.kind === 'source' ? 'border-orange-200 bg-orange-50/40' : v.kind === 'current' ? 'border-fuchsia-300 bg-fuchsia-50/40' : 'border-slate-200'}`} data-testid={`draft-version-${idx}`}>
    <div className="flex items-center gap-2 flex-wrap mb-1">
      <span className="font-mono font-semibold text-slate-800">{v.label}</span>
      {v.kind === 'source' && <Badge variant="outline" className="text-[10px] text-orange-700 border-orange-200">Production source</Badge>}
      {v.kind === 'current' && <Badge className="text-[10px] bg-fuchsia-600 text-white">Current</Badge>}
      {v.promoted_to_bom_id && <Badge className="text-[10px] bg-emerald-600 text-white">Promoted as rev {v.promoted_revision}</Badge>}
      <span className="text-[11px] text-slate-500 ml-auto">{fmt(v.saved_at)}{v.saved_by_name ? ` · ${v.saved_by_name}` : ''}</span>
    </div>
    <p className="text-[11px] text-slate-500 mb-2">{(v.lines || []).length} lines{v.notes ? ` · ${v.notes.slice(0, 80)}` : ''}</p>
    {idx > 0 && <LineDiff diff={v.diff_vs_previous} testId={`draft-version-diff-${idx}`} />}
  </div>
);

export const DraftHistoryDialog = ({ draft, onClose }) => {
  const { api } = useAuth();
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!draft) return;
    setData(null);
    api().get(`/rd/draft-boms/${draft.id}/history`).then(r => setData(r.data)).catch(() => setData({ versions: [] }));
  }, [api, draft]);

  return (
    <Dialog open={!!draft} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="draft-history-dialog">
        <DialogHeader><DialogTitle>Version trail · {draft?.name}</DialogTitle></DialogHeader>
        <Tabs defaultValue="versions">
          <TabsList>
            <TabsTrigger value="versions" data-testid="draft-history-tab-versions">Iterations &amp; changes</TabsTrigger>
            <TabsTrigger value="lineage" data-testid="draft-history-tab-lineage">Production lineage</TabsTrigger>
            <TabsTrigger value="approvals" data-testid="draft-history-tab-approvals">Approval trail</TabsTrigger>
          </TabsList>
          <TabsContent value="approvals" className="mt-3 space-y-2">
            {!(data?.approval_history || []).length ? <p className="text-sm text-slate-500" data-testid="approval-trail-empty">Not submitted for approval yet.</p>
              : [...data.approval_history].reverse().map((h, i) => (
                <div key={i} className="rounded-md border border-slate-200 p-2 text-xs" data-testid={`approval-trail-${i}`}>
                  <span className={`font-semibold uppercase tracking-wide mr-2 ${{ pending: 'text-amber-700', approved: 'text-emerald-700', rejected: 'text-rose-700' }[h.status] || 'text-slate-500'}`}>{h.status === 'pending' ? 'submitted' : h.status}</span>
                  <span className="text-slate-700">{h.by_name}</span> <span className="text-slate-400">· {fmt(h.at)} · {h.revision}</span>
                  {h.bom_revision && <span className="text-emerald-700"> → production rev {h.bom_revision}</span>}
                  {h.comment && <p className="text-slate-600 italic mt-0.5">“{h.comment}”</p>}
                </div>
              ))}
          </TabsContent>
          <TabsContent value="versions" className="mt-3 space-y-2">
            {!data ? <p className="text-sm text-slate-500">Loading…</p> : [...data.versions].reverse().map((v, i, arr) => (
              <VersionCard key={`${v.kind}-${v.iteration ?? 's'}`} v={v} idx={arr.length - 1 - i} />
            ))}
          </TabsContent>
          <TabsContent value="lineage" className="mt-3">
            {draft && <BomLineageTimeline draftId={draft.id} />}
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};
